<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'
import SongRow from './SongRow.vue'
import type { Student2Context } from '@/types/student2'
import { useStaticAsset } from '@/composables/useStaticAsset'

const asset = useStaticAsset()
const backgroundImage = `url('${asset('img/student-v2/staircase/cinderella_background.svg')}')`

const props = defineProps<{ context: Student2Context }>()

const characterActive = ref(false)
const hideIdleItem = ref(false)
const showOverlay = ref(false)

const container = useTemplateRef<HTMLElement>('container')
const list = useTemplateRef<HTMLElement>('list')
const overlay = useTemplateRef<HTMLElement>('overlay')
const resizeObserver = ref<ResizeObserver | null>(null)

const songs = computed(() => {
  return (props.context?.songs ?? []).map(s => {
    const currentUnitId = props.context?.smartbook?.last_unit_unlocked_by_progress
    const songKey = s.key
    const hasTail = (props.context?.student?.tails ?? []).some(
      t =>
        t.tail_type === 'control_item' &&
        (typeof t.custom_data?.song === 'string' ? t.custom_data.song : t.custom_data?.song?.key) === songKey
    )

    return {
      name: s.name,
      outline: hasTail || s.unit_id === currentUnitId,
      accepted: s.accepted
    }
  })
})

showOverlay.value = songs.value.every(e => e.accepted)

const relPxVert = (val: number) => (val / 780) * 100
const relPxHoriz = (val: number) => (val / 555) * 100

const resizeList = () => {
  if (!container.value || !list.value) {
    return
  }

  const rect = container.value.getBoundingClientRect()
  const width = window.matchMedia('(orientation: portrait)').matches ? rect.height : rect.width

  const widthFrac = width / 555
  list.value.style.transform = `scale(${widthFrac})`
}

const animatePromise = (
  el: Element,
  kf: Keyframe[] | PropertyIndexedKeyframes,
  opt?: number | KeyframeAnimationOptions
) => {
  return new Promise<void>(resolve => {
    const anim = el.animate(kf, opt)
    anim.onfinish = () => resolve()
  })
}

const animate = async () => {
  if (!overlay.value || !container.value) {
    return
  }

  // x, y - relative coordinates
  // h - relative height
  // f - flip
  const itemKeyframes = [
    { x: 228, y: 4, h: 57, f: 1 },
    { x: 256, y: 56, h: 57, f: 1 },
    { x: 303, y: 93, h: 57, f: 1 },
    { x: 337, y: 158, h: 57, f: 1 },
    { x: 358, y: 208, h: 57, f: 1 },
    { x: 359, y: 310, h: 57, f: 1 },
    { x: 321, y: 359, h: 57, f: 1 },
    { x: 251, y: 472, h: 57, f: 1 },
    { x: 223, y: 504, h: 57, f: 1 },
    { x: 223, y: 537, h: 57, f: 1 },
    { x: 238, y: 576, h: 42.75, f: 1 },
    { x: 266, y: 596, h: 42.75, f: 1 },
    { x: 299, y: 617, h: 42.75, f: 1 },
    { x: 326, y: 635, h: 42.75, f: 1 },
    { x: 355, y: 657, h: 42.75, f: 1 },
    { x: 312, y: 664, h: 43, f: -1 },
    { x: 459, y: 700, h: 42.75, f: -1 }
  ]

  const item = document.createElement('img')
  item.src = asset('img/student-v2/staircase/cinderella_item_moving.svg')
  item.style.position = 'absolute'
  item.style.zIndex = '3'

  const keyframes = itemKeyframes.map(frame => ({
    top: `${relPxVert(frame.y + 2)}%`,
    left: `${relPxHoriz(frame.x - 80)}%`,
    transform: `scaleX(${frame.f})`,
    height: `${relPxVert(frame.h + 22)}%`
  }))

  const options: KeyframeAnimationOptions = {
    duration: 8000,
    iterations: 1,
    fill: 'forwards',
    easing: 'ease'
  }

  hideIdleItem.value = true

  await animatePromise(overlay.value, [{ opacity: 1 }, { opacity: 0 }], { duration: 200 })
  showOverlay.value = false

  container.value.append(item)
  await animatePromise(item, keyframes, options)
  characterActive.value = true
  await animatePromise(item, [{ opacity: 1 }, { opacity: 0 }], { duration: 200 })
  item.remove()
}

const setActive = () => {
  hideIdleItem.value = true
  characterActive.value = true
}

onMounted(() => {
  if (!container.value) {
    return
  }

  resizeObserver.value = new ResizeObserver(resizeList)
  resizeObserver.value.observe(container.value)
  resizeList()
})

onBeforeUnmount(() => {
  resizeObserver.value?.disconnect()
})

defineExpose({ setActive, animate })
</script>

<template>
  <div ref="container" class="student-v2-cinderella">
    <div v-show="showOverlay" ref="overlay" class="student-v2-cinderella__overlay">
      <v-button mode="primary filled" @click="animate">
        <v-icon class="mr-1">mdi-party-popper</v-icon>
        Помочь Золушке
      </v-button>
    </div>

    <img
      class="student-v2-cinderella__character"
      :class="{ 'student-v2-cinderella__character_visible': characterActive }"
      :src="asset('img/student-v2/staircase/cinderella_character_active.svg')"
    />
    <img
      class="student-v2-cinderella__character"
      :class="{ 'student-v2-cinderella__character_visible': !characterActive }"
      :src="asset('img/student-v2/staircase/cinderella_character_idle.svg')"
    />

    <img
      v-show="!hideIdleItem"
      class="student-v2-cinderella__item-idle"
      :src="asset('img/student-v2/staircase/cinderella_item_idle.svg')"
    />

    <ul ref="list" class="student-v2-cinderella__songs-list">
      <ul style="gap: 14px">
        <li style="padding-left: 56px">
          <SongRow v-if="songs[15]" :song="songs[15]" />
        </li>
        <li style="padding-left: 80px">
          <SongRow v-if="songs[14]" :song="songs[14]" />
        </li>
        <li style="padding-left: 112px">
          <SongRow v-if="songs[13]" :song="songs[13]" />
        </li>
      </ul>
      <ul style="gap: 16px">
        <li style="padding-left: 120px">
          <SongRow v-if="songs[12]" :song="songs[12]" />
        </li>
        <li style="padding-left: 136px">
          <SongRow v-if="songs[11]" :song="songs[11]" />
        </li>
        <li style="padding-left: 152px">
          <SongRow v-if="songs[10]" :song="songs[10]" />
        </li>
      </ul>
      <ul style="gap: 14px">
        <li style="padding-left: 168px">
          <SongRow v-if="songs[9]" :song="songs[9]" />
        </li>
        <li style="padding-left: 152px">
          <SongRow v-if="songs[8]" :song="songs[8]" />
        </li>
        <li style="padding-left: 144px">
          <SongRow v-if="songs[7]" :song="songs[7]" />
        </li>
        <li style="padding-left: 136px">
          <SongRow v-if="songs[6]" :song="songs[6]" />
        </li>
      </ul>
      <ul style="gap: 19px">
        <li style="padding-left: 104px">
          <SongRow v-if="songs[5]" :song="songs[5]" />
        </li>
        <li style="padding-left: 96px">
          <SongRow v-if="songs[4]" :song="songs[4]" />
        </li>
        <li style="padding-left: 56px">
          <SongRow v-if="songs[3]" :song="songs[3]" />
        </li>
      </ul>
      <ul style="gap: 32px">
        <li style="padding-left: 24px">
          <SongRow v-if="songs[2]" :song="songs[2]" />
        </li>
        <li style="padding-left: 16px">
          <SongRow v-if="songs[1]" :song="songs[1]" />
        </li>
        <li style="padding-left: 0px">
          <SongRow v-if="songs[0]" :song="songs[0]" />
        </li>
      </ul>
    </ul>
  </div>
</template>

<style lang="scss">
@function relPxVert($val) {
  @return calc((#{$val} / 780) * 100%);
}

@function relPxHoriz($val) {
  @return calc((#{$val} / 555) * 100%);
}

.student-v2-cinderella {
  aspect-ratio: 555 / 780;
  max-width: min(555px, 90%);

  position: relative;
  overflow: hidden;
  background-image: v-bind(backgroundImage);
  background-size: cover;

  &__character {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    transition: opacity 0.2s;
    transition-delay: 0.2s;
    opacity: 0;
    z-index: 0;

    &_visible {
      transition-delay: 0s;
      opacity: 1;
      z-index: 1;
    }
  }

  &__item-idle {
    position: absolute;

    top: relPxVert(10);
    left: relPxHoriz(150);
    height: relPxVert(42);
  }

  &__songs-list {
    position: absolute;
    z-index: 2;
    top: relPxVert(62);
    left: relPxHoriz(80);

    transform-origin: left top;

    display: flex;
    flex-direction: column;
    gap: 19px;

    & > ul {
      display: flex;
      flex-direction: column;
    }

    li {
      height: 24px;
      font-size: 12px;
      line-height: 16px;
    }
  }

  &__overlay {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 5;
  }
}
</style>
